import React, { createContext, useContext, useState, useCallback } from 'react';
import { getDefaultDateRange } from '../utils/dateUtils';

const defaultDates = getDefaultDateRange();

const defaultFilters = {
  start_date: defaultDates.startDateStr,
  end_date: defaultDates.endDateStr,
  department: null,
  location: null,
  manager: null,
  pay_types: []  // Multi-select array of pay types
};

const FilterContext = createContext({
  filters: defaultFilters,
  setFilters: () => {},
  resetFilters: () => {},
  hasActiveFilters: false,
  activeFilterCount: 0
});

export const FilterProvider = ({ children }) => {
  const [filters, setFiltersState] = useState(defaultFilters);

  const setFilters = useCallback((newFilters) => {
    if (typeof newFilters === 'function') {
      setFiltersState(newFilters);
    } else {
      setFiltersState(prev => ({ ...prev, ...newFilters }));
    }
  }, []);

  const resetFilters = useCallback(() => {
    const defaultDates = getDefaultDateRange();
    setFiltersState({
      start_date: defaultDates.startDateStr,
      end_date: defaultDates.endDateStr,
      department: null,
      location: null,
      manager: null,
      pay_types: []
    });
  }, []);

  // Calculate if there are non-date filters active
  const hasActiveFilters = Boolean(
    filters.department || filters.location || filters.manager || (filters.pay_types && filters.pay_types.length > 0)
  );

  // Count active filters (excluding dates which are always set)
  const activeFilterCount = [
    filters.department,
    filters.location,
    filters.manager,
    filters.pay_types?.length > 0 ? true : null
  ].filter(Boolean).length;

  return (
    <FilterContext.Provider value={{
      filters,
      setFilters,
      resetFilters,
      hasActiveFilters,
      activeFilterCount,
      defaultFilters
    }}>
      {children}
    </FilterContext.Provider>
  );
};

export const useFilters = () => {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error('useFilters must be used within a FilterProvider');
  }
  return context;
};

export default FilterContext;
